import { useState, useEffect } from 'react';
import { settingsAPI, usersAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Save, Building, Users, DollarSign, Database, Download, Upload, Image, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCY_OPTIONS = [
  { symbol: 'Rs', code: 'LKR', name: 'Sri Lankan Rupee' },
  { symbol: '$', code: 'USD', name: 'US Dollar' },
  { symbol: '€', code: 'EUR', name: 'Euro' },
  { symbol: '£', code: 'GBP', name: 'British Pound' },
  { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
  { symbol: 'A$', code: 'AUD', name: 'Australian Dollar' },
  { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar' },
  { symbol: '¥', code: 'JPY', name: 'Japanese Yen' },
  { symbol: 'AED', code: 'AED', name: 'UAE Dirham' },
  { symbol: 'RM', code: 'MYR', name: 'Malaysian Ringgit' },
];

export default function SettingsPage() {
  const { user, isAdmin } = useAuth();
  const [settings, setSettings] = useState({
    business_name: '',
    business_address: '',
    business_phone: '',
    business_email: '',
    default_tax_rate: 0,
    invoice_prefix: 'INV',
    currency_symbol: 'Rs',
    currency_code: 'LKR'
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [headerImage, setHeaderImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const settingsRes = await settingsAPI.get();
        setSettings({
          ...settingsRes.data,
          currency_symbol: settingsRes.data.currency_symbol || 'Rs',
          currency_code: settingsRes.data.currency_code || 'LKR'
        });
        setHeaderImage(settingsRes.data.invoice_header_image || null);
        
        if (isAdmin) {
          const usersRes = await usersAPI.getAll();
          setUsers(usersRes.data);
        }
      } catch (error) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAdmin]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCurrencyChange = (code) => {
    const currency = CURRENCY_OPTIONS.find(c => c.code === code);
    if (currency) {
      setSettings({
        ...settings,
        currency_symbol: currency.symbol,
        currency_code: currency.code
      });
    }
  };

  const handleHeaderImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    setUploadingImage(true);
    try {
      const res = await settingsAPI.uploadHeaderImage(file);
      setHeaderImage(res.data.invoice_header_image);
      toast.success('Invoice header image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteHeaderImage = async () => {
    try {
      await settingsAPI.deleteHeaderImage();
      setHeaderImage(null);
      toast.success('Invoice header image removed');
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const handleUserStatusChange = async (userId, status) => {
    try {
      await usersAPI.updateStatus(userId, status);
      setUsers(users.map(u => u.id === userId ? { ...u, status } : u));
      toast.success('User status updated');
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleUserRoleChange = async (userId, role) => {
    try {
      await usersAPI.updateRole(userId, role);
      setUsers(users.map(u => u.id === userId ? { ...u, role } : u));
      toast.success('User role updated');
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black font-heading tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Configure your garage settings</p>
      </div>

      {/* Business Settings */}
      <Card data-testid="business-settings-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="font-heading">Business Information</CardTitle>
              <CardDescription>This information appears on invoices</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="business_name">Business Name</Label>
              <Input
                id="business_name"
                value={settings.business_name}
                onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
                placeholder="Your Garage Name"
                data-testid="business-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="business_phone">Phone</Label>
              <Input
                id="business_phone"
                value={settings.business_phone}
                onChange={(e) => setSettings({ ...settings, business_phone: e.target.value })}
                placeholder="+94 77 123 4567"
                data-testid="business-phone-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="business_address">Address</Label>
            <Input
              id="business_address"
              value={settings.business_address}
              onChange={(e) => setSettings({ ...settings, business_address: e.target.value })}
              placeholder="123 Main St, Colombo"
              data-testid="business-address-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="business_email">Email</Label>
            <Input
              id="business_email"
              type="email"
              value={settings.business_email}
              onChange={(e) => setSettings({ ...settings, business_email: e.target.value })}
              placeholder="contact@yourgarage.com"
              data-testid="business-email-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Invoice Header Image */}
      <Card data-testid="invoice-header-image-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
              <Image className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <CardTitle className="font-heading">Invoice Header Image</CardTitle>
              <CardDescription>This image replaces the top section of printed invoices</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {headerImage ? (
            <div className="space-y-3">
              <div className="border rounded-lg p-3 bg-white">
                <img
                  src={headerImage}
                  alt="Invoice Header"
                  className="max-h-32 w-auto mx-auto object-contain"
                  data-testid="invoice-header-preview"
                />
              </div>
              <div className="flex gap-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleHeaderImageUpload}
                    className="hidden"
                    data-testid="header-image-replace-input"
                  />
                  <Button variant="outline" asChild disabled={uploadingImage}>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingImage ? 'Uploading...' : 'Replace Image'}
                    </span>
                  </Button>
                </label>
                <Button variant="outline" className="text-red-500 hover:text-red-600" onClick={handleDeleteHeaderImage} data-testid="header-image-delete-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Image className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground mb-3">Upload an image for your invoice header (max 2MB)</p>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleHeaderImageUpload}
                  className="hidden"
                  data-testid="header-image-upload-input"
                />
                <Button variant="outline" asChild disabled={uploadingImage}>
                  <span>
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingImage ? 'Uploading...' : 'Upload Image'}
                  </span>
                </Button>
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice & Currency Settings */}
      <Card data-testid="invoice-settings-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <CardTitle className="font-heading">Invoice & Currency Settings</CardTitle>
              <CardDescription>Default settings for invoices</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select 
                value={settings.currency_code} 
                onValueChange={handleCurrencyChange}
              >
                <SelectTrigger data-testid="currency-select">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.symbol} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Current: {settings.currency_symbol} ({settings.currency_code})
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoice_prefix">Invoice Prefix</Label>
              <Input
                id="invoice_prefix"
                value={settings.invoice_prefix}
                onChange={(e) => setSettings({ ...settings, invoice_prefix: e.target.value.toUpperCase() })}
                placeholder="INV"
                maxLength={5}
                data-testid="invoice-prefix-input"
              />
              <p className="text-xs text-muted-foreground">Example: {settings.invoice_prefix || 'INV'}-000001</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="default_tax_rate">Default Tax Rate (%)</Label>
              <Input
                id="default_tax_rate"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={settings.default_tax_rate}
                onChange={(e) => setSettings({ ...settings, default_tax_rate: parseFloat(e.target.value) || 0 })}
                data-testid="tax-rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Currency Symbol Preview</Label>
              <div className="p-3 bg-slate-100 rounded-lg font-mono text-lg">
                {settings.currency_symbol} 1,234.56
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="font-semibold uppercase tracking-wide"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Database Export (Admin Only) */}
      {isAdmin && (
        <Card data-testid="database-export-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <CardTitle className="font-heading">Deployment Packages</CardTitle>
                <CardDescription>Download deployment packages for your hosting</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Backend - Railway + MySQL */}
            <div className="bg-slate-50 p-4 rounded-lg space-y-4">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900">Backend → Railway.com + MySQL</span>
                <Badge className="badge-info">Recommended</Badge>
              </div>
              <div className="text-sm text-muted-foreground">
                <p className="mb-2">Complete backend package for Railway deployment with MySQL:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Create a new project on Railway.com</li>
                  <li>Add MySQL database from Railway's marketplace</li>
                  <li>Deploy the backend code from this package</li>
                  <li>Set environment variables (DATABASE_URL, JWT_SECRET, CORS_ORIGINS)</li>
                </ol>
              </div>
              <div className="flex gap-3">
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/download/railway-mysql`}
                  download="garagepro-railway-mysql.zip"
                  className="inline-flex"
                >
                  <Button variant="default" data-testid="download-railway-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Download Railway Package
                  </Button>
                </a>
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/download/schema`}
                  download="garagepro_schema.sql"
                  className="inline-flex"
                >
                  <Button variant="outline" data-testid="download-schema-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Schema Only
                  </Button>
                </a>
              </div>
            </div>
            
            {/* Frontend - Vercel */}
            <div className="bg-blue-50 p-4 rounded-lg space-y-4">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-900">Frontend → Vercel</span>
              </div>
              <div className="text-sm text-muted-foreground">
                <p className="mb-2">Deploy frontend to Vercel:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Download the frontend package below</li>
                  <li>Push to your GitHub repository</li>
                  <li>Connect the repository to Vercel</li>
                  <li>Set REACT_APP_BACKEND_URL to your Railway backend URL</li>
                </ol>
              </div>
              <div className="flex gap-3">
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/download/vercel-frontend`}
                  download="garagepro-vercel-frontend.zip"
                  className="inline-flex"
                >
                  <Button variant="default" data-testid="download-vercel-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Download Frontend Package
                  </Button>
                </a>
              </div>
              <div className="text-xs font-mono bg-blue-100 p-3 rounded">
                <p className="font-semibold mb-1">Required Environment Variable:</p>
                <code>REACT_APP_BACKEND_URL=https://your-backend.railway.app</code>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Management (Admin Only) */}
      {isAdmin && (
        <Card data-testid="user-management-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="font-heading">User Management</CardTitle>
                <CardDescription>Manage user accounts and permissions</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      {u.id === user.id ? (
                        <Badge className="badge-info">{u.role}</Badge>
                      ) : (
                        <Select
                          value={u.role}
                          onValueChange={(role) => handleUserRoleChange(u.id, role)}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="staff">Staff</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={u.status === 'active' ? 'badge-success' : 'badge-danger'}>
                        {u.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {u.id !== user.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUserStatusChange(u.id, u.status === 'active' ? 'inactive' : 'active')}
                          data-testid={`toggle-user-${u.id}`}
                        >
                          {u.status === 'active' ? 'Disable' : 'Enable'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
